import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import FloatingEffects from './components/FloatingEffects';

// 添加字体导入
const GlobalStyle = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@300;400;500;700&display=swap');
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  font-family: 'Quicksand', 'Noto Sans SC', sans-serif;
`;

const Card = styled(motion.div)`
  padding: 40px;
  border-radius: 0px;
  max-width: 800px;
  width: 90%;
  text-align: center;
  position: relative;
  z-index: 2;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const BackgroundImage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  z-index: 1;
  @media (max-width: 768px) {
    background-position: ${props => {
      if (props.image === '/images/image_1.jpg') return 'calc(50% + 200px) center';
      if (props.image === '/images/image_8.jpg') return 'left center';
      return 'center';
    }};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1;
`;

const Title = styled.h1`
  font-family: 'Quicksand', 'Noto Sans SC', sans-serif;
  font-size: 2.2rem;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  font-weight: 500;
  letter-spacing: 0.5px;
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.h2`
  font-family: 'Quicksand', 'Noto Sans SC', sans-serif;
  font-size: 1.6rem;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  font-weight: 400;
  letter-spacing: 0.3px;
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Message = styled.p`
  font-family: 'Quicksand', 'Noto Sans SC', sans-serif;
  font-size: 1.3rem;
  line-height: 1.6;
  color: #ffffff;
  margin-bottom: 30px;
  white-space: pre-line;
  font-weight: 300;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.2px;
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  a {
    color: #ffffff;
    font-size: 1.5rem;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
`;

const Navigation = styled.div`
  position: fixed;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 3;
  pointer-events: none;
`;

const NavButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: none;
  transition: all 0.2s ease;
  color: #ffffff;
  pointer-events: auto;
  box-shadow: none;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(1.2);
  }

  svg {
    width: 32px;
    height: 32px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`;

const MusicControl = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 3;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: none;
  transition: all 0.2s ease;
  color: #ffffff;
  box-shadow: none;
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(1.2)'};
  }

  svg {
    width: 32px;
    height: 32px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const PhotoItem = styled.div`
  position: relative;
  padding-bottom: 100%;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const PhotoImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PhotoCaption = styled.p`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  font-size: 1rem;
  text-align: center;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  transition: opacity 0.3s ease;
  opacity: ${props => props.show ? 1 : 0};
  pointer-events: ${props => props.show ? 'auto' : 'none'};
`;

const LoadingText = styled.div`
  color: white;
  font-size: 1.5rem;
  font-family: 'Quicksand', 'Noto Sans SC', sans-serif;
`;

const pages = [
  {
    title: "祝小宝宝壮壮的大宝宝妈妈第一个母亲节快乐",
    subtitle: "给世界上最棒的妈妈，第一年就满分！",
    message: "",
    background: "/images/image_1.jpg"
  },
  {
    title: "来自壮壮的信",
    message: `妈妈，今天是你的第一个母亲节！

谢谢你每天抱着我、喂我、陪我、爱我。

我还不会说话，但我已经用力地在笑啦，哈哈哈嘎嘎嘎！

那是我给你最真心的礼物呀～

爱你爱你爱你 ！

爱你爱你爱你 ！

爱你爱你爱你 ！

你的小壮壮 么么哒！`,
    background: "/images/image_2.jpg"
  },
  {
    title: "来自爸爸的感谢",
    message: `在壮壮出生之前我就知道你会是个满分妈妈，温柔坚强又有爱心

谢谢你日复一日地陪伴壮壮、亲亲抱抱他

壮壮出生之后的这些日子虽然累，但是很快乐

我很幸运，你是壮壮是你的宝贝，你也是我的宝贝呀

第一个母亲节快乐呀！

爱你爱你爱你 ！

爱你爱你爱你 ！

爱你爱你爱你 ！

你的牛牛 么么哒！`,
    background: "/images/image_3.jpg"
  },
  {
    title: "选一个你想要的母亲节礼物吧～（当然，全选也可以 😘）",
    message: "",
    background: "/images/image_4.jpg"
  },
  {
    title: "SPA - 壮壮交给我，你尽情享受放松吧",
    message: "",
    background: "/images/image_5.jpg"
  },
  {
    title: "Tiffany Blue® Double Heart Tag Pendant - 把我们的名字刻在项链上吧",
    message: "",
    background: "/images/image_6.jpg"
  },
  {
    title: "声波饰品 - 记录下壮壮叫的第一声妈妈，永久保存吧",
    message: "",
    background: "/images/image_7.jpg"
  },
  {
    title: "定制书 - 我有的，你也必须要有",
    message: "",
    background: "/images/image_8.jpg"
  },
  {
    title: "自己选！你说什么就是什么！",
    message: "",
    background: "/images/image_9.jpg"
  },
  {
    title: "祝我的宝宝母亲节快乐",
    message: `不论接下来有多少个母亲节，

第一个永远最特别。

愿你拥有被爱、被感激、被呵护的每一天。

爱你的

壮壮 & 牛牛`,
    background: "/images/image_10.jpg"
  },
  {
    title: "现在选择你的礼物吧！",
    message: `1. <a href="https://www.japanesemountainretreat.com.au/" target="_blank">SPA - Japanese Mountain Retreat Mineral Springs & Spa</a>

2. <a href="https://www.tiffany.com.au/jewelry/necklaces-pendants/return-to-tiffany-mini-double-heart-tag-pendant-GRP06366/" target="_blank">Tiffany Blue® Double Heart Tag Pendant</a>

3. <a href="https://www.etsy.com/au/listing/1676916164/personalized-sound-wave-necklace-custom?ls=r&ref=hp_rv-1&frs=1&sts=1&content_source=82aac417b97c7cc58f569e3212210bfadc3978e8%253A1676916164&logging_key=82aac417b97c7cc58f569e3212210bfadc3978e8%3A1676916164" target="_blank">Personalized Sound Wave Necklace</a>`,
    background: "/images/image_90.jpg"
  }
];

function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasCompletedFirstRound, setHasCompletedFirstRound] = useState(false);
  const [hasReachedGiftPage, setHasReachedGiftPage] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const audioRef = useRef(null);
  const hasAttemptedAutoPlay = useRef(false);
  const autoPlayAttempts = useRef(0);

  // 预加载所有图片
  useEffect(() => {
    const imageUrls = pages.map(page => page.background);
    let loadedCount = 0;

    const preloadImages = imageUrls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          loadedCount++;
          if (loadedCount === imageUrls.length) {
            setImagesLoaded(true);
          }
          resolve();
        };
        img.onerror = reject;
      });
    });

    Promise.all(preloadImages).catch(error => {
      console.error('Error preloading images:', error);
      setImagesLoaded(true); // 即使加载失败也继续显示
    });
  }, []);

  // 预加载音频
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'auto';
    
    const handleCanPlayThrough = () => {
      console.log('Audio can play through');
      setAudioLoaded(true);
      audioRef.current = audio;
      
      // 设置音频属性
      audio.loop = true;
      audio.volume = 0.5;
      
      // 标记音频准备就绪
      setAudioReady(true);
    };

    const handleError = (error) => {
      console.error('Error loading audio:', error);
      setAudioLoaded(true);
      setAudioReady(true);
    };

    const handleLoadStart = () => {
      console.log('Audio loading started');
    };

    const handleProgress = () => {
      console.log('Audio loading progress:', audio.buffered.length > 0 ? 'Buffered' : 'Loading');
    };

    const handleLoadedData = () => {
      console.log('Audio data loaded');
    };

    const handleLoadedMetadata = () => {
      console.log('Audio metadata loaded');
    };

    // 添加所有事件监听器
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    // 开始加载音频
    audio.src = '/music.mp3';
    audio.load();

    return () => {
      // 移除所有事件监听器
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // 添加键盘快捷键
  useEffect(() => {
    const handleKeyPress = (event) => {
      switch(event.key) {
        case 'ArrowLeft':
          handlePageChange('prev');
          break;
        case 'ArrowRight':
          handlePageChange('next');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // 尝试自动播放
  const attemptAutoPlay = async () => {
    if (!audioRef.current || !audioReady || hasAttemptedAutoPlay.current) return;
    
    console.log('Attempting auto play, attempt:', autoPlayAttempts.current + 1);
    
    try {
      // 尝试直接播放
      await audioRef.current.play();
      console.log('Auto play successful');
      setIsPlaying(true);
      hasAttemptedAutoPlay.current = true;
    } catch (error) {
      console.error('Auto play failed:', error);
      autoPlayAttempts.current += 1;
      
      // 如果直接播放失败，尝试设置音量后播放
      if (autoPlayAttempts.current === 1) {
        try {
          audioRef.current.volume = 0;
          await audioRef.current.play();
          audioRef.current.volume = 0.5;
          console.log('Auto play successful with volume trick');
          setIsPlaying(true);
          hasAttemptedAutoPlay.current = true;
        } catch (error) {
          console.error('Auto play with volume trick failed:', error);
        }
      }
    }
  };

  // 监听音频准备状态
  useEffect(() => {
    if (!audioReady) return;

    console.log('Audio is ready, attempting to play');
    
    // 尝试多次自动播放
    const timers = [
      setTimeout(attemptAutoPlay, 1000),  // 1秒后尝试
      setTimeout(attemptAutoPlay, 2000),  // 2秒后尝试
      setTimeout(attemptAutoPlay, 3000),  // 3秒后尝试
    ];

    // 添加用户交互事件监听
    const handleUserInteraction = () => {
      if (!hasAttemptedAutoPlay.current) {
        attemptAutoPlay();
      }
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    return () => {
      timers.forEach(clearTimeout);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [audioReady]);

  // 添加自动翻页功能
  useEffect(() => {
    let timer;
    if (!hasCompletedFirstRound && !hasReachedGiftPage && currentPage < pages.length - 1) {
      timer = setInterval(() => {
        setCurrentPage((prev) => {
          const nextPage = (prev + 1) % pages.length;
          if (nextPage === 0) {
            setHasCompletedFirstRound(true);
          }
          return nextPage;
        });
      }, 8000); // 每8秒切换一次页面
    }
    return () => clearInterval(timer);
  }, [hasCompletedFirstRound, currentPage, hasReachedGiftPage]);

  // 处理音乐开关
  const toggleMusic = async () => {
    if (!audioRef.current || !audioReady) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Audio toggle error:', error);
      setIsPlaying(false);
    }
  };

  // 处理页面切换
  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      if (prev === pages.length - 1) {
        setHasReachedGiftPage(true);
        if (direction === 'prev') return pages.length - 2; // 回到"祝我的宝宝母亲节快乐"页面
        return 0; // 回到第一页
      }
      const nextPage = (prev + (direction === 'next' ? 1 : -1) + pages.length) % pages.length;
      if (nextPage === 0) {
        setHasCompletedFirstRound(true);
      }
      return nextPage;
    });
  };

  // 在 useEffect 中修改音频加载逻辑
  // 在 useEffect 中修改音频加载逻辑
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleCanPlayThrough = () => {
      setAudioReady(true);
      if (isPlaying) {
        audio.play().catch(err => {
          console.error('播放失败:', err);
          setAudioReady(false);
        });
      }
    };

    const handleError = (e) => {
      console.error('音频加载错误:', e);
      setAudioReady(false);
    };

    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('error', handleError);
    };
  }, [isPlaying]);

  // 在 handlePlayMusic 中修改播放逻辑
  const handlePlayMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audioReady) {
      audio.play().catch(err => {
        console.error('播放失败:', err);
        setAudioReady(false);
      });
      setIsPlaying(true);
    } else {
      console.log('音频尚未加载完成，请稍后再试');
    }
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <LoadingOverlay show={!imagesLoaded || !audioReady}>
          <LoadingText>加载中...</LoadingText>
        </LoadingOverlay>
        <BackgroundImage 
          image={pages[currentPage].background} 
          style={{ opacity: imagesLoaded ? 1 : 0 }}
        />
        <Overlay />
        
        <MusicControl onClick={toggleMusic} disabled={!audioReady}>
          {isPlaying ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          )}
        </MusicControl>

        <AnimatePresence mode="wait">
          <Card
            key={currentPage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <Title>{pages[currentPage].title}</Title>
            {pages[currentPage].subtitle && <Subtitle>{pages[currentPage].subtitle}</Subtitle>}
            <Message dangerouslySetInnerHTML={{ __html: pages[currentPage].message }} />
          </Card>
        </AnimatePresence>

        {(hasCompletedFirstRound || currentPage >= pages.length - 1) && (
          <Navigation>
            <NavButton onClick={() => handlePageChange('prev')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </NavButton>
            <NavButton onClick={() => handlePageChange('next')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </NavButton>
          </Navigation>
        )}
        <FloatingEffects />
      </Container>
    </>
  );
}

export default App; 